import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Busy, PageHeader, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar } from '@/components/inputs';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import { PRODUCT_FIELDS } from '@/shared/fields';
import { committed, rows } from '@/shared/data';
import { BUILT_IN_KEYS, PLANS, money, productTerms } from '@/shared/pricing';
import {
  BUILT_IN_SEEDS,
  STORE_SEEDS,
  FRONT_STYLES,
  PRIVATE_KEYS,
  SALE_STYLES,
  frontState,
  isBuiltIn,
  isPrivate,
  productPath,
  saleState,
} from '@/shared/products';

export const route = { path: '/admin/products', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'ShoppingBag', label: 'Products', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 46 };

const SHOW = ['On front page', 'Hidden', 'Private link'];
const KINDS = ['Built-in plans', 'Custom products'];

function PriceCell({ product }) {
  if (isBuiltIn(product)) return <span className="text-muted-foreground">Plan pricing</span>;
  const terms = productTerms(product);
  if (!terms.length) return <Dash />;
  return (
    <span className="tabular-nums">
      {terms.map((t) => `${t.months === 0 ? 'once' : t.id} ${money(t.price)}`).join(' · ')}
    </span>
  );
}

export default function AdminProducts() {
  const navigate = useNavigate();
  const [show, setShow] = useState('all');
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [adding, setAdding] = useState('');

  const { data, loading, error, run } = useFetch(
    () => sdk.table('products_c').select(PRODUCT_FIELDS).orderBy('sort_c').fetch().then(rows),
    [],
  );

  const all = data ?? [];
  const missing = BUILT_IN_KEYS.filter((k) => !all.some((p) => p.plan_key_c === k));
  const missingPublic = missing.filter((k) => !PRIVATE_KEYS.includes(k));
  const missingPrivate = missing.filter((k) => PRIVATE_KEYS.includes(k));
  const missingItems = Object.keys(STORE_SEEDS).filter((slug) => !all.some((p) => p.slug_c === slug));
  const q = search.trim().toLowerCase();
  const list = all.filter((p) => {
    if (show !== 'all' && frontState(p) !== show) return false;
    if (kind === 'Built-in plans' && !isBuiltIn(p)) return false;
    if (kind === 'Custom products' && isBuiltIn(p)) return false;
    if (q && !`${p.Name} ${p.slug_c} ${p.tagline_c ?? ''}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const filtered = show !== 'all' || kind !== 'all' || !!q;

  function clear() {
    setSearch('');
    setShow('all');
    setKind('all');
  }

  async function seedBuiltIns() {
    setSeeding(true);
    try {
      let added = 0;
      for (const key of missing) {
        const res = await sdk.table('products_c').create(BUILT_IN_SEEDS[key]);
        if (committed(res)) added += 1;
      }
      if (added) toast.success(`${added === 1 ? 'Plan' : 'Plans'} added to Products`);
      run();
    } finally {
      setSeeding(false);
    }
  }

  async function addItem(slug) {
    const seed = STORE_SEEDS[slug];
    if (!seed) return;
    setAdding(slug);
    try {
      const res = await sdk.table('products_c').create(seed);
      if (committed(res, `${seed.Name} added to the store`)) run();
    } finally {
      setAdding('');
    }
  }

  const columns = [
    {
      label: 'Product',
      render: (p) => (
        <RowLink to={`/admin/products/${p.Id}`} sub={productPath(p)}>
          {p.Name}
        </RowLink>
      ),
    },
    {
      label: 'Kind',
      className: 'hidden md:table-cell text-muted-foreground',
      render: (p) =>
        isPrivate(p)
          ? `Private plan · ${PLANS[p.plan_key_c]?.name ?? p.plan_key_c}`
          : isBuiltIn(p)
            ? `Built-in · ${PLANS[p.plan_key_c]?.name ?? p.plan_key_c}`
            : 'Custom',
    },
    { label: 'Prices', className: 'hidden lg:table-cell', render: (p) => <PriceCell product={p} /> },
    { label: 'Front page', render: (p) => <StatusBadge value={frontState(p)} styles={FRONT_STYLES} /> },
    {
      label: 'Sale',
      className: 'hidden sm:table-cell',
      render: (p) => <StatusBadge value={saleState(p)} styles={SALE_STYLES} />,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Store"
        title="Products"
        description="Everything the storefront sells. Hide one from the front page and its own link still works."
        actions={
          <>
            {data !== undefined &&
              missingItems.map((slug) => (
                <Button key={slug} variant="outline" onClick={() => addItem(slug)} disabled={!!adding}>
                  <Busy
                    busy={adding === slug}
                    label={
                      <>
                        <ApperIcon name="PackagePlus" size={16} />
                        Add {STORE_SEEDS[slug].Name}
                      </>
                    }
                    busyLabel="Adding…"
                  />
                </Button>
              ))}
            {missing.length > 0 && data !== undefined && (
              <Button variant="outline" onClick={seedBuiltIns} disabled={seeding}>
                <Busy
                  busy={seeding}
                  label={
                    <>
                      <ApperIcon name="Layers" size={16} />
                      Set up built-in plans
                    </>
                  }
                  busyLabel="Adding…"
                />
              </Button>
            )}
            <Button asChild>
              <Link to="/admin/products/new">
                <ApperIcon name="Plus" size={16} />
                New product
              </Link>
            </Button>
          </>
        }
      />

      {missing.length > 0 && data !== undefined && (
        <p className="mb-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {[
            missingPublic.length > 0 &&
              `${missingPublic.map((k) => PLANS[k].name).join(', ')} ${missingPublic.length === 1 ? 'is' : 'are'} not in this list yet, so ${missingPublic.length === 1 ? 'it shows' : 'they show'} on the front page as usual.`,
            missingPrivate.length > 0 &&
              `${missingPrivate.map((k) => PLANS[k].name).join(', ')} ${missingPrivate.length === 1 ? 'is' : 'are'} not in this list yet. ${missingPrivate.length === 1 ? 'It stays' : 'They stay'} private on ${missingPrivate.length === 1 ? 'its' : 'their'} own link and never ${missingPrivate.length === 1 ? 'shows' : 'show'} on the front page or in search.`,
            'Set up built-in plans to manage them here.',
          ]
            .filter(Boolean)
            .join(' ')}
        </p>
      )}

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search name or address…" />
        <FilterSelect value={show} onChange={setShow} values={SHOW} allLabel="Shown and hidden" />
        <FilterSelect value={kind} onChange={setKind} values={KINDS} allLabel="All kinds" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'ShoppingBag',
          title: filtered ? 'No products match' : 'No products yet',
          description: filtered
            ? 'Nothing fits these filters.'
            : 'Add a product with its own page and term prices, or set up the built-in plans to manage them here.',
          action: filtered ? clear : () => navigate('/admin/products/new'),
          actionLabel: filtered ? 'Clear filters' : 'New product',
        }}
      >
        <DataTable columns={columns} rows={list} />
      </ListBody>
    </div>
  );
}
